/**
 * Settings — the SINGLE account surface for SmarterHoops.
 *
 * Build 191 consolidates account management here. The topbar avatar is
 * now display-only; this page is reached exclusively via the gear icon.
 *
 * Fields:
 *   - Name           editable, defaults to "Team Manager" if Apple
 *                    didn't share the user's name on first SIWA.
 *   - Apple Email    display-only, sourced from Apple's identity_token
 *                    at sign-up (`apple_email_at_signup`); falls back to
 *                    the user's primary email when that's a real address
 *                    (admins). Synthetic `@privaterelay.smarterhoops.app`
 *                    placeholders are hidden — there's nothing useful
 *                    there to show the user.
 *   - Account ID     read-only with copy.
 *   - Sign Out       soft logout — clears the local session + secure
 *                    storage, calls `POST /auth/logout`, lands on
 *                    /login so a different Apple ID can take over.
 *   - Delete Account unchanged (Apple 5.1.1(v) compliance).
 *
 * Removed in build 191: the "Contact Email" field (we use the Apple
 * email for everything support-adjacent).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { clearAuthToken } from '@/lib/api';
import { removeItem as secureRemoveItem } from '@/lib/secureStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  User as UserIcon,
  Mail,
  Hash,
  Copy,
  Trash2,
  LogOut,
} from 'lucide-react';

const SYNTHETIC_DOMAINS = ['@device.smarterhoops.app', '@privaterelay.smarterhoops.app'];
const DEVICE_ID_KEY = 'smarthoops_device_id';
const REQUIRED_CONFIRM = 'DELETE';

function isSyntheticEmail(email) {
  if (typeof email !== 'string') return false;
  const lower = email.toLowerCase();
  return SYNTHETIC_DOMAINS.some((d) => lower.endsWith(d));
}

/**
 * Pick the email we actually want to surface in the UI:
 *   1. `apple_email_at_signup` — what Apple told us on first SIWA
 *      (could be a real email, an Apple Hide-My-Email relay, or null
 *      if the user opted out).
 *   2. `email` — only if it's not one of our synthetic placeholders.
 * Returns null when nothing user-facing exists.
 */
function pickDisplayEmail(user) {
  if (!user) return null;
  if (user.apple_email_at_signup) return user.apple_email_at_signup;
  if (user.email && !isSyntheticEmail(user.email)) return user.email;
  return null;
}

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  // Sign-out state
  const [signingOut, setSigningOut] = useState(false);

  // Delete-account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api
      .get('/auth/me')
      .then((r) => {
        setUser(r.data);
        setName(r.data.name || '');
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/users/me', { name: name.trim() });
      setUser((prev) => ({ ...prev, name: name.trim() }));
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyAccountId = () => {
    if (!user?.account_id) return;
    navigator.clipboard.writeText(user.account_id);
    toast.success('Account ID copied');
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      // Best-effort server-side session invalidation. Even if this fails
      // (network, expired token), we still wipe local state so the user
      // is effectively signed out of the device.
      try {
        await api.post('/auth/logout');
      } catch {
        /* ignore — we still clear local creds below */
      }
      clearAuthToken();
      // Drop any legacy device UUID so /login → fresh SIWA truly behaves
      // as a clean account switch (no auto-bounce into the old account
      // via a stale auth path).
      secureRemoveItem(DEVICE_ID_KEY);
      try { localStorage.removeItem(DEVICE_ID_KEY); } catch { /* ignore */ }
      // Hard reload to /login so the bootstrap re-evaluates auth on a
      // clean slate — same pattern ProtectedRoute uses on auth failure.
      window.location.replace('/login');
    } finally {
      setSigningOut(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmText !== REQUIRED_CONFIRM || deleting) return;
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      clearAuthToken();
      secureRemoveItem(DEVICE_ID_KEY);
      try { localStorage.removeItem(DEVICE_ID_KEY); } catch { /* ignore */ }
      setShowDeleteModal(false);
      toast.success('Your account has been deleted.');
      // Hard reload to /login so the bootstrap re-evaluates auth on a
      // clean slate — same pattern as sign-out. NEVER route to the
      // legacy /welcome page; that path mints a fresh device-UUID
      // account and defeats the purpose of having deleted.
      window.location.replace('/login');
    } catch (err) {
      console.error('Account deletion failed:', err);
      toast.error('Couldn\u2019t delete your account. Please try again or email support@smarterhoops.com.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="settings-loading">
        <div className="animate-spin h-10 w-10 rounded-full border-4 border-slate-900 border-t-transparent" />
      </div>
    );
  }

  const displayEmail = pickDisplayEmail(user);
  const isAdmin = user?.role === 'admin';

  return (
    <div className="max-w-2xl mx-auto" data-testid="settings-page">
      <h1
        className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-5"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        data-testid="settings-title"
      >
        Settings
      </h1>

      {/* ── PROFILE ── */}
      <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="space-y-3">
          {/* Name */}
          <div>
            <Label
              htmlFor="settings-name"
              className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 flex items-center gap-1.5"
            >
              <UserIcon className="w-3.5 h-3.5" /> Name
            </Label>
            <Input
              id="settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-10"
              data-testid="settings-name-input"
            />
          </div>

          {/* Apple Email (display-only, hidden when only synthetic) */}
          {displayEmail && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {isAdmin ? 'Sign-in Email' : 'Apple Email'}
              </Label>
              <Input
                value={displayEmail}
                disabled
                className="h-10 bg-slate-50 text-slate-500"
                data-testid="settings-apple-email"
              />
              {!isAdmin && (
                <p className="text-[11px] text-slate-400 mt-1">
                  This is the email Apple shared with us at sign-in. We use it for support.
                </p>
              )}
            </div>
          )}

          {/* Account ID */}
          {user?.account_id && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" /> Account ID
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={user.account_id}
                  disabled
                  className="h-10 bg-slate-50 text-slate-700 font-mono tracking-wider"
                  data-testid="settings-account-id"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-3"
                  onClick={handleCopyAccountId}
                  data-testid="settings-copy-account-id"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="mt-4 h-10 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold uppercase tracking-[.07em] text-[15px] w-full sm:w-auto"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="settings-save-button"
        >
          {saving ? 'Saving\u2026' : 'Save Changes'}
        </Button>
      </form>

      {/* ── ACCOUNT (Sign Out + Delete) ──
          Hidden for admins: admin lifecycle is managed via the /admin
          web console, not the in-app account flow. */}
      {!isAdmin && (
        <section className="mt-8 pt-6 border-t border-slate-200" data-testid="settings-account-section">
          <h2
            className="text-[11px] font-bold uppercase tracking-[.12em] text-slate-500 mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Account
          </h2>

          {/* Sign Out — soft logout, swap Apple ID */}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            data-testid="settings-sign-out-button"
            className="w-full h-12 rounded-full inline-flex items-center justify-center gap-2 active:scale-[.98] transition-transform disabled:opacity-60"
            style={{
              background: '#0B1824',
              color: '#fff',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 15,
            }}
          >
            <LogOut className="w-4 h-4" />
            {signingOut ? 'Signing out\u2026' : 'Sign Out'}
          </button>
          <p className="mt-2 text-[12px] text-slate-500">
            Signs you out of this device. Your account and data stay safe — sign back in
            anytime, or use a different Apple ID to switch accounts.
          </p>

          {/* Delete Account — destructive */}
          <button
            type="button"
            onClick={openDeleteModal}
            data-testid="settings-delete-account-button"
            className="mt-5 w-full h-12 rounded-full inline-flex items-center justify-center gap-2 active:scale-[.98] transition-transform"
            style={{
              background: '#DC2626',
              color: '#fff',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 15,
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete Account
          </button>
          <p className="mt-2 text-[12px] text-slate-500">
            This permanently removes your account and all your team and game data.
          </p>
        </section>
      )}

      {/* ── DELETE CONFIRMATION MODAL ── */}
      <Dialog
        open={showDeleteModal}
        onOpenChange={(open) => {
          if (deleting) return;
          setShowDeleteModal(open);
          if (!open) setDeleteConfirmText('');
        }}
      >
        <DialogContent data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle
              className="text-[20px]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              Delete your account?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 mt-2 leading-relaxed">
              This will permanently delete your account, all your teams, players, games, practice plans, and drills. This action cannot be undone and your data cannot be recovered.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <Label
              htmlFor="delete-confirm-input"
              className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 block"
            >
              Type DELETE to confirm
            </Label>
            <Input
              id="delete-confirm-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="h-11 font-mono tracking-wider"
              data-testid="delete-confirm-input"
            />
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => !deleting && setShowDeleteModal(false)}
              disabled={deleting}
              data-testid="delete-cancel-button"
              className="h-11 rounded-full active:scale-[.98] transition-transform disabled:opacity-60"
              style={{
                background: '#F1F5F9',
                color: '#475569',
                border: 'none',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting || deleteConfirmText !== REQUIRED_CONFIRM}
              data-testid="delete-confirm-button"
              className="h-12 rounded-full active:scale-[.98] transition-transform disabled:opacity-40 disabled:active:scale-100"
              style={{
                background: '#DC2626',
                color: '#fff',
                border: 'none',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 15,
              }}
            >
              {deleting ? 'Deleting\u2026' : 'Delete Account'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
